/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

import { SegmentedControl, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { useCountdown } from '../hooks/useCountdown';
import type { LaunchBoardUnit } from '../types';
import { formatCountdown } from './LaunchBoardGrid';
import { formatUnitPrice } from './LaunchUnitCard';
import {
  ALLOCATION_STATUS_DOT,
  ALLOCATION_STATUS_TILE,
  URGENT_COUNTDOWN_SECONDS,
  countdownColor,
} from './launch-unit-status';
import type { AllocationStatus } from '../types';

const STATUS_ORDER: AllocationStatus[] = [
  'available',
  'held',
  'reserved_with_deposit',
  'sold',
];

interface LaunchBoardMobileListProps {
  units: LaunchBoardUnit[];
  currentUserEmail?: string;
  onSelectUnit: (unit: LaunchBoardUnit) => void;
}

/** A tower and the units inside it. */
interface Tower {
  name: string;
  units: LaunchBoardUnit[];
  available: number;
}

/** Groups units by tower, preserving the order they arrive in. */
function groupByTower(units: LaunchBoardUnit[]): Tower[] {
  const towers = new Map<string, LaunchBoardUnit[]>();
  units.forEach((unit) => {
    const key = unit.tower ?? '';
    towers.set(key, [...(towers.get(key) ?? []), unit]);
  });
  return [...towers.entries()].map(([name, list]) => ({
    name,
    units: list,
    available: list.filter((u) => u.allocation_status === 'available').length,
  }));
}

/** Groups a tower's units by floor, ordered high to low. */
function groupByFloor(units: LaunchBoardUnit[]): Array<[string, LaunchBoardUnit[]]> {
  const floors = new Map<string, LaunchBoardUnit[]>();
  units.forEach((unit) => {
    const key = unit.floor ?? '';
    floors.set(key, [...(floors.get(key) ?? []), unit]);
  });
  return [...floors.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
}

/**
 * The broker's own live reservations, pinned above everything else.
 *
 * First thing on the screen because it is the only part of the board with a
 * deadline attached to it.
 */
function MyReservations({ units, onSelectUnit }: {
  units: LaunchBoardUnit[];
  onSelectUnit: (unit: LaunchBoardUnit) => void;
}) {
  const { t } = useTranslation('features/launches');
  if (units.length === 0) return null;

  return (
    <div className="flex flex-col">
      <Text
        as="span"
        className="py-[var(--space-2)] text-[length:var(--text-meta)] text-[var(--text-tertiary)]"
      >
        {t('board.myReservationsTitle')}
      </Text>
      {units.map((unit) => (
        <ReservationRow key={unit.unit_id} unit={unit} onSelect={onSelectUnit} />
      ))}
    </div>
  );
}

/**
 * One of the broker's live reservations, as the prototype lists them:
 * which unit, for whom, and how long is left — one row each, rather than
 * a single aggregate line. A broker holding three units needs to know
 * which one is about to lapse, not that something is.
 */
function ReservationRow({ unit, onSelect }: {
  unit: LaunchBoardUnit;
  onSelect: (unit: LaunchBoardUnit) => void;
}) {
  const { t } = useTranslation('features/launches');
  const remaining = useCountdown(unit.remaining_seconds);

  return (
    <button
      type="button"
      onClick={() => onSelect(unit)}
      className="flex min-h-[var(--space-16)] w-full items-center justify-between gap-[var(--space-3)] rounded-(--radius-md) px-[var(--space-3)] text-left"
      style={{
        background: 'var(--launch-status-held-bg)',
        border: '1px solid var(--launch-status-held-border)',
        marginBottom: 'var(--space-2)',
      }}
    >
      <span className="flex min-w-0 flex-col">
        <Text variant="bodySm" as="span" className="truncate text-[var(--text-primary)]">
          {[unit.tower, unit.label].filter(Boolean).join(' · ')}
        </Text>
        {unit.held_for_client_name && (
          <Text variant="meta" as="span" className="truncate text-[var(--text-tertiary)]">
            {t('board.heldFor', { client: unit.held_for_client_name })}
          </Text>
        )}
      </span>
      <Text
        as="span"
        className="shrink-0 text-[length:var(--text-stat)] tabular-nums [font-weight:var(--font-semibold)]"
        style={{ color: countdownColor(remaining) }}
      >
        {formatCountdown(remaining)}
      </Text>
    </button>
  );
}

/**
 * Sticky tower switch plus status chips, which double as the legend.
 */
function BoardFilterBar({ towers, activeTower, onTowerChange, counts, totalCount, activeStatus, onStatusChange }: {
  towers: Tower[];
  activeTower: string;
  onTowerChange: (name: string) => void;
  counts: Record<string, number>;
  totalCount: number;
  activeStatus: AllocationStatus | null;
  onStatusChange: (status: AllocationStatus | null) => void;
}) {
  const { t } = useTranslation('features/launches');

  return (
    <div className="sticky top-0 z-10 flex flex-col gap-[var(--space-2)] bg-[var(--surface-primary)] py-[var(--space-2)]">
      {/* The shared control, not a hand-rolled one: ds-015 forbids
          reimplementing this, and a bespoke copy drifts from the rest of
          the app the moment the design system moves. */}
      {towers.length > 1 && (
        <SegmentedControl
          value={activeTower}
          onChange={onTowerChange}
          items={towers.map((tower) => ({
            value: tower.name,
            label: `${tower.name || t('board.singleTower')} · ${t('board.freeCount', { count: tower.available })}`,
          }))}
          variant="pill"
          size="sm"
          ariaLabel={t('board.towerLabel')}
        />
      )}
      {/* "Todas" leads the row, as the prototype has it: clearing the
          filter is a chip, not a second tap on the active one. Labels are
          the short forms — four long ones do not fit 375px without
          scrolling past the first. */}
      <div className="flex gap-[var(--space-2)] overflow-x-auto">
        <button
          type="button"
          onClick={() => onStatusChange(null)}
          className={cn(
            'flex min-h-[var(--space-12)] shrink-0 items-center gap-[var(--space-2)] rounded-(--radius-full) border px-[var(--space-3)] text-[length:var(--text-meta)]',
            activeStatus === null
              ? 'border-[var(--border-strong)] bg-[var(--surface-tertiary)]'
              : 'border-[var(--border-subtle)]',
          )}
        >
          <span className="text-[var(--text-secondary)]">{t('board.statusShort.all')}</span>
          <span className="tabular-nums text-[var(--text-primary)]">{totalCount}</span>
        </button>

        {STATUS_ORDER.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => onStatusChange(activeStatus === status ? null : status)}
            className={cn(
              'flex min-h-[var(--space-12)] shrink-0 items-center gap-[var(--space-2)] rounded-(--radius-full) border px-[var(--space-3)] text-[length:var(--text-meta)]',
              activeStatus === status
                ? 'border-[var(--border-strong)] bg-[var(--surface-tertiary)]'
                : 'border-[var(--border-subtle)]',
            )}
          >
            <span className={cn('inline-block h-2 w-2 rounded-full', ALLOCATION_STATUS_DOT[status])} />
            <span className="text-[var(--text-secondary)]">{t(`board.statusShort.${status}`)}</span>
            <span className="tabular-nums text-[var(--text-primary)]">{counts[status] ?? 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The unit number without its tower prefix.
 *
 * The tower is already the selected tab above, so repeating it in every
 * row costs width that 375px does not have — the prototype shows "601",
 * not "Torre A-601".
 */
function bareLabel(unit: LaunchBoardUnit): string {
  const tower = unit.tower ?? '';
  if (!tower) return unit.label;
  return unit.label.replace(new RegExp(`^${tower}[-\\s·]*`), '') || unit.label;
}

/**
 * One unit as a 64px row: the shape a thumb can hit and an eye can scan.
 */
function UnitRow({ unit, onSelect }: {
  unit: LaunchBoardUnit;
  onSelect: (unit: LaunchBoardUnit) => void;
}) {
  const { t } = useTranslation('features/launches');
  const held = unit.allocation_status === 'held';
  const ticking = useCountdown(held ? unit.remaining_seconds : null);

  return (
    <button
      type="button"
      onClick={() => onSelect(unit)}
      className="flex min-h-[var(--space-16)] w-full items-center gap-[var(--space-3)] border-b border-[var(--border-ultra-subtle)] px-[var(--space-1)] text-left"
    >
      <span
        className={cn(
          'flex h-[var(--space-12)] w-[var(--space-12)] shrink-0 items-center justify-center rounded-(--radius-md) border text-[length:var(--text-small)] [font-weight:var(--font-semibold)]',
          ALLOCATION_STATUS_TILE[unit.allocation_status],
        )}
      >
        {bareLabel(unit)}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <Text variant="bodySm" as="span" className="truncate text-[var(--text-primary)]">
          {unit.typology || t('board.noTypology')}
        </Text>
        <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
          {formatUnitPrice(unit.list_price)}
        </Text>
      </span>

      {held ? (
        <span className="flex shrink-0 items-center gap-[var(--space-1)]">
          <Clock size={14} color={countdownColor(ticking)} />
          <Text
            as="span"
            className="text-[length:var(--text-stat)] tabular-nums [font-weight:var(--font-semibold)]"
            style={{ color: countdownColor(ticking) }}
          >
            {formatCountdown(ticking)}
          </Text>
        </span>
      ) : (
        <Text variant="meta" as="span" className="shrink-0 text-[var(--text-tertiary)]">
          {t(`status.${unit.allocation_status}`)}
        </Text>
      )}
    </button>
  );
}

/**
 * The board as a list, for the phone.
 *
 * A grid of tiles is a desktop shape: at 375px two columns leave a unit card
 * too narrow for label, typology, price and a countdown at once. Rows group
 * by floor the way a sales mirror is read, and the countdown — the only
 * number on this screen with a deadline — is the largest thing in the row.
 */
export function LaunchBoardMobileList({ units, currentUserEmail, onSelectUnit }: LaunchBoardMobileListProps) {
  const { t } = useTranslation('features/launches');
  const towers = useMemo(() => groupByTower(units), [units]);
  const [activeTower, setActiveTower] = useState<string>(towers[0]?.name ?? '');
  const [activeStatus, setActiveStatus] = useState<AllocationStatus | null>(null);

  const counts = useMemo(() => {
    const acc: Record<string, number> = {};
    units.forEach((u) => { acc[u.allocation_status] = (acc[u.allocation_status] ?? 0) + 1; });
    return acc;
  }, [units]);

  const mine = useMemo(
    () => units.filter((u) => u.allocation_status === 'held'
      && u.held_by_broker_id === currentUserEmail),
    [units, currentUserEmail],
  );

  const tower = towers.find((tw) => tw.name === activeTower) ?? towers[0];
  const visible = (tower?.units ?? []).filter(
    (u) => activeStatus === null || u.allocation_status === activeStatus,
  );
  const floors = groupByFloor(visible);

  return (
    <div data-testid="launch-board-mobile" className="flex flex-col gap-[var(--space-3)]">
      <MyReservations units={mine} onSelectUnit={onSelectUnit} />

      <BoardFilterBar
        towers={towers}
        activeTower={tower?.name ?? ''}
        onTowerChange={setActiveTower}
        counts={counts}
        totalCount={units.length}
        activeStatus={activeStatus}
        onStatusChange={setActiveStatus}
      />

      {floors.map(([floor, floorUnits]) => (
        <div key={floor} className="flex flex-col">
          <Text
            as="span"
            className="py-[var(--space-2)] text-[length:var(--text-meta)] text-[var(--text-tertiary)]"
          >
            {t('board.floorHeading', {
              floor,
              available: floorUnits.filter((u) => u.allocation_status === 'available').length,
              total: floorUnits.length,
            })}
          </Text>
          {floorUnits.map((unit) => (
            <UnitRow key={unit.unit_id} unit={unit} onSelect={onSelectUnit} />
          ))}
        </div>
      ))}

      {visible.length === 0 && (
        <Text variant="bodySm" as="span" className="py-[var(--space-6)] text-center text-[var(--text-tertiary)]">
          {t('board.noUnitsForFilter')}
        </Text>
      )}
    </div>
  );
}
